import { CdfGrid } from '@commonComponents/data/grid/CdfGrid/CdfGrid.tsx';
import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RowSelectionMode } from '@models/enums/RowSelectionMode';
import { ApiCallType } from '@api/ApiCalls.ts';
import { Text } from '@models/DomainModels/Master/text.ts';
import { gridColumnDefinitions } from './grid-column-definitions';
import classNames from 'classnames';
import styles from './TextComponent.module.scss';
import { TextDetailDialogWrapper } from './TextDetailDialog';
import type { EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

import Grid from '@mui/material/Grid2';
import { ThemeProvider, Typography } from '@mui/material';
import { DynamicStore } from '@state/interfaces/idynamic-store';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useInjectReducers } from '@hooks/useInjectReducers';
import { useTextsPermissions } from '@hooks/permissions/useTextsPermissions';
import { Provider } from 'react-redux';
import { PermissionProvider } from '@auth/PermissionProvider';
import brzTheme from '@brz/brz-global-mui-theme';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@commonComponents/core/ErrorFallback';

interface TextComponentProps {
  readonly organizationId: string;
  readonly store: DynamicStore | undefined;
}

function TextComponent({ organizationId, store }: Readonly<TextComponentProps>) {
  const { writePermission } = useTextsPermissions(organizationId);
  const { t } = useTranslation(['texts', 'enums']);
  const gridRef = useRef<{
    refresh: () => void;
    addCreatedItem: (item: Text) => void;
    updateModifiedItem: (item: Text) => void;
  }>(null);
  const selectedIdsRef = useRef<string[]>([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedTextData, setSelectedTextData] = useState<Text | undefined>(undefined);

  const handleProfileClick = useCallback((eventOrData: { data?: Text }) => {
    const data = eventOrData?.data;
    if (!data?.id) return;
    selectedIdsRef.current = [data.id];
    setSelectedTextData(data);
    setProfileDialogOpen(true);
  }, []);

  const handleProfileClose = useCallback((result: EntityDialogCloseResult<Text>) => {
    setProfileDialogOpen(false);

    if (result.action === 'created' && result.data && gridRef.current) {
      gridRef.current.addCreatedItem(result.data);
    } else if (result.action === 'updated' && result.data && gridRef.current) {
      gridRef.current.updateModifiedItem(result.data);
    }

    setSelectedTextData(undefined);
  }, []);

  /**
   * Memoized column definitions based on translation
   */
  const columnDefs = useCallback(() => gridColumnDefinitions({ t }), [t]);

  /**
   * Handle clicking the add button
   */
  const handleAddButtonClick = useCallback(() => {
    setSelectedTextData(undefined);
    setProfileDialogOpen(true);
  }, []);

  return (
    <Grid className={styles.textComponent}>
      <Grid>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('title')}
        </Typography>
      </Grid>
      <Grid container className={styles.content}>
        <Grid className={classNames(styles.grid)}>
          <CdfGrid<Text>
            ref={gridRef}
            apiCallType={ApiCallType.FetchTexts}
            columnDefs={columnDefs()}
            rowSelection={RowSelectionMode.Multiple}
            showAddButton={writePermission}
            showDeleteButton={writePermission}
            showDeleteButtonInGrid={writePermission}
            onAddClick={handleAddButtonClick}
            addButtonText={t('addButtonText')}
            onDoubleClick={handleProfileClick}
            data-testid="text-grid"
          />
        </Grid>
      </Grid>
      {profileDialogOpen && (
        <TextDetailDialogWrapper
          textData={selectedTextData}
          open={profileDialogOpen}
          onClose={handleProfileClose}
          organizationId={organizationId}
          store={store}
        />
      )}
    </Grid>
  );
}

/**
 * Props interface for TextWrapper
 */
interface TextComponentWrapperProps {
  /** Organization ID for permission checks and API calls */
  readonly organizationId: string;
  /** Redux store for state management */
  readonly store: DynamicStore | undefined;
}

/**
 * Wrapper component for TextComponent.
 * Provides Theme, Localization, Redux Store, Permissions, and Error Boundary.
 * Handles reducer injection if necessary.
 *
 * @category Components/Shared/Text
 */
export function TextComponentWrapper({ organizationId, store }: Readonly<TextComponentWrapperProps>) {
  // Assuming reducer injection is needed, adjust if not
  const reducerInjected = useInjectReducers(store, organizationId);
  if (!reducerInjected) {
    return null;
  }

  return (
    <ThemeProvider theme={brzTheme}>
      <LocalizationProvider>
        <Provider store={store}>
          <PermissionProvider>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <TextComponent organizationId={organizationId} store={store} />
            </ErrorBoundary>
          </PermissionProvider>
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
